import { apiFetch } from '../client';
import type {
    TenantDetailWire,
    CreateTenantRequestLocal,
    CreateTenantResponseLocal,
    UpdateTenantRequestLocal,
    VerifyTenantInfraResponseLocal,
    PlatformTenantStatusLocal,
    PlatformRoleListItemLocal,
    PlatformRoleListResponseLocal,
    PlatformRoleDetailLocal,
    CreatePlatformRoleRequestLocal,
    UpdatePlatformRoleRequestLocal,
    PlatformUserDetailLocal,
    PlatformUserListItemLocal,
    CreatePlatformUserRequestLocal,
    CreatePlatformUserResponseLocal,
    UpdatePlatformUserRequestLocal,
    AssignPlatformRoleRequestLocal,
    PlatformRoleAssignmentItemLocal,
    PlatformRoleAssignmentListResponseLocal,
    TenantTypeInfoLocal,
    TenantTypeListResponseLocal,
    TenantTypeCatalogLocal,
    UpdateTenantTypeCatalogRequestLocal,
} from '../types/PlatformLocal';
import type { Permission } from '../types/permissions';
import {
    normalizePlatformTenantList,
    normalizePlatformTenantDetail,
} from '../types/platformNormalizers';
// Users del tenant en el módulo Platform: REUSAN los tipos *Local y los normalizers del flujo
// tenant (mismos endpoints reales `/tenants/{tenantId}/users...`). NO se duplican tipos (TD-WEBAPP-001).
import type {
    TenantUserDetailLocal,
    CreateTenantUserRequestLocal,
    CreateTenantUserResponseLocal,
    UpdateTenantUserRequestLocal,
    CreateTenantRoleRequestLocal,
    UpdateTenantRoleRequestLocal,
} from '../types/TenantLocal';
import type {
    TenantUserListWire,
    TenantUserDetailWire,
    TenantRoleListWire,
} from '../types/TenantWire';
import {
    normalizeTenantUserList,
    normalizeTenantUserDetail,
    normalizeTenantRoleList,
} from '../types/tenantNormalizers';
// Branding del tenant (drill-down) y del PLATFORM (tenantId='platform'): mismo endpoint real
// `/tenants/{tenantId}/branding` (GET/PUT — verificado en backoffice.gen.ts: backofficeGetTenantBranding /
// backofficeUpdateTenantBranding). NO existe `/platform/.../branding`. Se REUSAN el tipo *Local, el wire
// y el normalizer del flujo tenant (TD-WEBAPP-001) — no se duplican.
import type { TenantBrandingLocal } from '../types/TenantLocal';
import type { BrandingConfigWire } from '../types/TenantWire';
import { normalizeBranding } from '../types/tenantNormalizers';
// Auditoría PLATFORM (`GET /platform/audit`, permiso `platform.audit.view`, scope PLATFORM). El shape de
// fila es el MISMO `SharedAuditLogRow` que la auditoría de tenant (changeId/changeType/actorCognitoSub/
// entityType/entityId/before/after/ipAddress/userAgent) → REUSAMOS el wire, los params y el normalizer del
// flujo tenant (TD-WEBAPP-001), no se duplican tipos. Diferencia de scope: no lleva `{tenantId}` en el path
// y los entityType resueltos client-side son los de plataforma (Tenant/PlatformUser/Role).
import type { AuditQueryParamsLocal } from '../types/TenantLocal';
import type { AuditQueryWire } from '../types/TenantWire';
import { normalizeAuditQuery } from '../types/tenantNormalizers';

interface RequestOpts {
    idempotencyKey?: string;
}

interface ListParams {
    cursor?: string;
    pageSize?: number;
}

// Mismo helper de query string que tenant.ts/retail.ts (duplicado local por consistencia del repo).
function toSearch(params: Record<string, string | number | undefined>): string {
    const entries = Object.entries(params).filter(([, v]) => v !== undefined);
    if (entries.length === 0) return '';
    const usp = new URLSearchParams(entries.map(([k, v]) => [k, String(v)]));
    return `?${usp.toString()}`;
}

// `withIdempotency` NO es helper compartido: está duplicado local en tenant.ts/retail.ts. Se copia igual.
function withIdempotency(opts: RequestOpts): RequestInit {
    return opts.idempotencyKey ? { headers: { 'Idempotency-Key': opts.idempotencyKey } } : {};
}

interface PlatformTenantListWire {
    items?: TenantDetailWire[];
}

/** El backend (TenantStatusChangeResponse) trae su propio envelope; apiFetch ya desenvuelve `data`. */
interface TenantStatusChangeDataWire {
    status: PlatformTenantStatusLocal;
}

/**
 * Wire de `GET /platform/roles` (envelope ya desenvuelto por apiFetch → `data`). El backend devuelve
 * `RoleListItem` genérico: `permissions` viene como `string[]` y `isSystem` opcional. Lo estrechamos a
 * `PlatformRoleListItemLocal` (permissions: Permission[]) en el normalizer de abajo — no inventamos
 * shape, solo narrowing del union de permisos.
 */
interface PlatformRoleListWire {
    items?: {
        roleId: string;
        displayName: string;
        description?: string;
        scope: PlatformRoleListItemLocal['scope'];
        isSystem?: boolean;
        permissions?: string[];
    }[];
}

function normalizePlatformRoleList(wire: PlatformRoleListWire): PlatformRoleListResponseLocal {
    return {
        items: (wire.items ?? []).map((r) => ({
            roleId: r.roleId,
            displayName: r.displayName,
            description: r.description,
            scope: r.scope,
            isSystem: r.isSystem,
            // El catálogo de permisos del backend ES el union Permission; el list los trae como string[]
            // genérico. Narrowing seguro (el backend solo emite claves del enum). DEC-007.
            permissions: r.permissions as Permission[] | undefined,
        })),
    };
}

export const platformApi = {
    listTenants: () =>
        apiFetch<PlatformTenantListWire>('/platform/tenants').then(normalizePlatformTenantList),

    getTenant: (tenantId: string) =>
        apiFetch<TenantDetailWire>(`/platform/tenants/${tenantId}`).then(
            normalizePlatformTenantDetail,
        ),

    createTenant: (
        body: CreateTenantRequestLocal,
        opts: RequestOpts = {},
    ): Promise<CreateTenantResponseLocal> =>
        apiFetch<CreateTenantResponseLocal>('/platform/tenants', {
            method: 'POST',
            body: JSON.stringify(body),
            ...withIdempotency(opts),
        }),

    updateTenant: (tenantId: string, body: UpdateTenantRequestLocal) =>
        apiFetch<TenantDetailWire>(`/platform/tenants/${tenantId}`, {
            method: 'PUT',
            body: JSON.stringify(body),
        }).then(normalizePlatformTenantDetail),

    suspendTenant: (tenantId: string) =>
        apiFetch<TenantStatusChangeDataWire>(`/platform/tenants/${tenantId}/suspend`, {
            method: 'POST',
        }),

    activateTenant: (tenantId: string) =>
        apiFetch<TenantStatusChangeDataWire>(`/platform/tenants/${tenantId}/activate`, {
            method: 'POST',
        }),

    // DEC-RBAC-059: re-verifica la infra del silo (DescribeTable de las tablas DDB requeridas). Si ya
    // existen todas → el backend siembra los roles diferidos y deja el tenant `active`; si faltan,
    // devuelve la lista actualizada. Idempotente. No-op si el tenant no está `pending-infra`. apiFetch
    // ya desenvuelve `data`. Tipo LOCAL hasta que el backend llegue a develop (sync-api-types).
    verifyTenantInfra: (tenantId: string): Promise<VerifyTenantInfraResponseLocal> =>
        apiFetch<VerifyTenantInfraResponseLocal>(`/platform/tenants/${tenantId}/verify-infra`, {
            method: 'POST',
        }),

    // ─── Roles PLATFORM ───
    // Endpoints `/platform/roles[/{roleId}]` (verbos verificados en backoffice.gen.ts: GET/POST en la
    // colección, PUT/DELETE en el item). NO son los roles del tenant (`/tenants/{tid}/roles`): estos
    // viven a nivel plataforma. El scope del rol lo AUTOCALCULA el backend (DEC-037) e `isSystem` se
    // fuerza false al crear → el form solo manda displayName/description/permissions.
    listPlatformRoles: (): Promise<PlatformRoleListResponseLocal> =>
        apiFetch<PlatformRoleListWire>('/platform/roles').then(normalizePlatformRoleList),

    createPlatformRole: (
        body: CreatePlatformRoleRequestLocal,
        opts: RequestOpts = {},
    ): Promise<PlatformRoleDetailLocal> =>
        apiFetch<PlatformRoleDetailLocal>('/platform/roles', {
            method: 'POST',
            body: JSON.stringify(body),
            ...withIdempotency(opts),
        }),

    updatePlatformRole: (
        roleId: string,
        body: UpdatePlatformRoleRequestLocal,
    ): Promise<PlatformRoleDetailLocal> =>
        apiFetch<PlatformRoleDetailLocal>(`/platform/roles/${roleId}`, {
            method: 'PUT',
            body: JSON.stringify(body),
        }),

    deletePlatformRole: (roleId: string) =>
        apiFetch<{ success: true }>(`/platform/roles/${roleId}`, { method: 'DELETE' }),

    // ─── Administradores PLATFORM (`/platform/users/*`) ───
    // CRUD de admins a nivel plataforma. El contrato coincide con el openapi generado (verificado en
    // backoffice.gen.ts), así que se consumen los wire shapes directo (sin normalizer: los timestamps
    // epoch ms se rinden en la UI tal cual o con un formateo local en el componente). El alta NO lleva
    // rol ni scope — la asignación de rol es el flujo `*AdminRole` de más abajo. El platform admin con
    // scope PLATFORM '*' alcanza estos endpoints; el backend re-enforza (`platform.user.*`).
    listPlatformAdmins: (): Promise<{ items: PlatformUserListItemLocal[] }> =>
        apiFetch<{ items?: PlatformUserListItemLocal[] }>('/platform/users').then((w) => ({
            items: w.items ?? [],
        })),

    createPlatformAdmin: (
        body: CreatePlatformUserRequestLocal,
        opts: RequestOpts = {},
    ): Promise<CreatePlatformUserResponseLocal> =>
        apiFetch<CreatePlatformUserResponseLocal>('/platform/users', {
            method: 'POST',
            body: JSON.stringify(body),
            ...withIdempotency(opts),
        }),

    getPlatformAdmin: (cognitoSub: string): Promise<PlatformUserDetailLocal> =>
        apiFetch<PlatformUserDetailLocal>(`/platform/users/${cognitoSub}`),

    updatePlatformAdmin: (
        cognitoSub: string,
        body: UpdatePlatformUserRequestLocal,
    ): Promise<PlatformUserDetailLocal> =>
        apiFetch<PlatformUserDetailLocal>(`/platform/users/${cognitoSub}`, {
            method: 'PUT',
            body: JSON.stringify(body),
        }),

    disablePlatformAdmin: (cognitoSub: string) =>
        apiFetch<{ success: true }>(`/platform/users/${cognitoSub}/disable`, { method: 'POST' }),

    enablePlatformAdmin: (cognitoSub: string) =>
        apiFetch<{ success: true }>(`/platform/users/${cognitoSub}/enable`, { method: 'POST' }),

    // DELETE sin body: `DELETE /platform/users/{cognitoSub}` (verificado en backoffice.gen.ts:
    // backofficeDeletePlatformUser). Permiso `platform.user.delete`, scope PLATFORM. El backend re-enforza.
    deletePlatformAdmin: (cognitoSub: string) =>
        apiFetch<{ success: true }>(`/platform/users/${cognitoSub}`, { method: 'DELETE' }),

    // ─── Asignación de rol con scope a un admin platform ───
    // `POST .../role-assignments` body `{ roleId, scopeRef, expiresAt? }`. El backend RECALCULA el
    // effective scope desde el ROL (220f7b4): un rol PLATFORM → global (scopeRef '*'); un rol acotado a
    // un tenant → scopeRef = tenantId. Por eso el AssignRoleDialog manda: global → scopeRef '*'; por
    // tenant → un assign por tenant con scopeRef = tenantId. El backend valida (anti-escalación DEC-026).
    listAdminRoleAssignments: (
        cognitoSub: string,
    ): Promise<PlatformRoleAssignmentListResponseLocal> =>
        apiFetch<{ items?: PlatformRoleAssignmentItemLocal[] }>(
            `/platform/users/${cognitoSub}/role-assignments`,
        ).then((w) => ({ items: w.items ?? [] })),

    assignAdminRole: (
        cognitoSub: string,
        body: AssignPlatformRoleRequestLocal,
        opts: RequestOpts = {},
    ): Promise<{ assignmentId: string }> =>
        apiFetch<{ assignmentId: string }>(`/platform/users/${cognitoSub}/role-assignments`, {
            method: 'POST',
            body: JSON.stringify(body),
            ...withIdempotency(opts),
        }),

    revokeAdminRole: (cognitoSub: string, assignmentId: string) =>
        apiFetch<{ success: true }>(
            `/platform/users/${cognitoSub}/role-assignments/${assignmentId}/revoke`,
            { method: 'POST' },
        ),

    // ─── Tipos de tenant (`/platform/tenant-types/*`) ───
    // Un tipo de tenant (hoy solo `retail`) define el TECHO de permisos disponibles para TODOS los
    // tenants de ese tipo. Gateados por `rbac.catalog.manage` (scope PLATFORM). Tipos *Local
    // hand-rolleados hasta el sync de openapi (TD-WEBAPP-001) — apiFetch ya desenvuelve `data`.
    getTenantTypes: (): Promise<TenantTypeListResponseLocal> =>
        apiFetch<{ items?: TenantTypeInfoLocal[] }>('/platform/tenant-types').then((w) => ({
            items: w.items ?? [],
        })),

    // GET catalog devuelve `{ tenantType, keys, available }` (envelope ya desenvuelto por apiFetch):
    // `keys` = techo actual del tipo (marcados); `available` = universo de permisos asignables a un
    // tipo de tenant (todos los no-platform), que el editor usa para renderizar los checkboxes. NO se
    // usa el catálogo contextual (`/catalog/permissions`) porque para un admin platform devuelve SOLO
    // platform.* (DEC-073) → el editor quedaba vacío.
    getTenantTypeCatalog: (tenantType: string): Promise<TenantTypeCatalogLocal> =>
        apiFetch<TenantTypeCatalogLocal>(`/platform/tenant-types/${tenantType}/catalog`),

    updateTenantTypeCatalog: (
        tenantType: string,
        body: UpdateTenantTypeCatalogRequestLocal,
    ): Promise<TenantTypeCatalogLocal> =>
        apiFetch<TenantTypeCatalogLocal>(`/platform/tenant-types/${tenantType}/catalog`, {
            method: 'PUT',
            body: JSON.stringify(body),
        }),

    // ─── Users del tenant (drill-down) ───
    // El `tenantId` viene del PATH del drill-down (param de ruta), NO del user logueado. Los endpoints
    // reales son `/tenants/{tenantId}/users...` (el backoffice openapi NO expone /platform/tenants/{id}/users):
    // un platform admin con scope PLATFORM '*' los alcanza (rank PLATFORM cubre TENANT) y el backend
    // re-enforza. Mismo contrato/normalizers que tenantApi.* — se reusan, no se duplican.
    listTenantUsers: (tenantId: string, params: ListParams = {}) =>
        apiFetch<TenantUserListWire>(`/tenants/${tenantId}/users${toSearch(params)}`).then(
            normalizeTenantUserList,
        ),

    getTenantUser: (tenantId: string, cognitoSub: string): Promise<TenantUserDetailLocal> =>
        apiFetch<TenantUserDetailWire>(`/tenants/${tenantId}/users/${cognitoSub}`).then(
            normalizeTenantUserDetail,
        ),

    createTenantUser: (
        tenantId: string,
        body: CreateTenantUserRequestLocal,
        opts: RequestOpts = {},
    ): Promise<CreateTenantUserResponseLocal> =>
        apiFetch<CreateTenantUserResponseLocal>(`/tenants/${tenantId}/users`, {
            method: 'POST',
            body: JSON.stringify(body),
            ...withIdempotency(opts),
        }),

    updateTenantUser: (tenantId: string, cognitoSub: string, body: UpdateTenantUserRequestLocal) =>
        apiFetch<TenantUserDetailLocal>(`/tenants/${tenantId}/users/${cognitoSub}`, {
            method: 'PUT',
            body: JSON.stringify(body),
        }),

    disableTenantUser: (tenantId: string, cognitoSub: string) =>
        apiFetch<{ success: true }>(`/tenants/${tenantId}/users/${cognitoSub}/disable`, {
            method: 'POST',
        }),

    // DELETE sin body: mismo endpoint real que tenantApi.deleteUser, pero sobre el `tenantId` del
    // drill-down (permiso `tenant.user.delete`, scope TENANT). El platform admin lo cubre por rank;
    // el backend re-enforza. No requiere tipo generado.
    deleteTenantUser: (tenantId: string, cognitoSub: string) =>
        apiFetch<{ success: true }>(`/tenants/${tenantId}/users/${cognitoSub}`, {
            method: 'DELETE',
        }),

    enableTenantUser: (tenantId: string, cognitoSub: string) =>
        apiFetch<{ success: true }>(`/tenants/${tenantId}/users/${cognitoSub}/enable`, {
            method: 'POST',
        }),

    resetPasswordTenantUser: (tenantId: string, cognitoSub: string) =>
        apiFetch<{ temporaryPassword: string }>(
            `/tenants/${tenantId}/users/${cognitoSub}/password-reset`,
            { method: 'POST' },
        ),

    resetMfaTenantUser: (tenantId: string, cognitoSub: string) =>
        apiFetch<{ status: 'MFA_RESET' }>(`/tenants/${tenantId}/users/${cognitoSub}/mfa-reset`, {
            method: 'POST',
        }),

    // Reenvía la invitación (regenera contraseña temporal + manda el correo branded). Solo INVITED.
    resendInvitationTenantUser: (tenantId: string, cognitoSub: string) =>
        apiFetch<{ status: 'INVITATION_RESENT' }>(
            `/tenants/${tenantId}/users/${cognitoSub}/resend-invitation`,
            { method: 'POST' },
        ),

    // Roles del tenant objetivo: alimentan el <select> de rol al crear un usuario en el drill-down
    // Y la pestaña Roles del drill-down. Reusa el normalizer/endpoint del flujo tenant
    // (`/tenants/{tenantId}/roles`). Crear/editar/borrar son los mismos verbos que tenantApi.*Role
    // (el platform admin alcanza el scope TENANT por rank; el backend re-enforza).
    listTenantRoles: (tenantId: string) =>
        apiFetch<TenantRoleListWire>(`/tenants/${tenantId}/roles`).then(normalizeTenantRoleList),

    createTenantRole: (
        tenantId: string,
        body: CreateTenantRoleRequestLocal,
        opts: RequestOpts = {},
    ) =>
        apiFetch<{ roleId: string }>(`/tenants/${tenantId}/roles`, {
            method: 'POST',
            body: JSON.stringify(body),
            ...withIdempotency(opts),
        }),

    updateTenantRole: (tenantId: string, roleId: string, body: UpdateTenantRoleRequestLocal) =>
        apiFetch<{ roleId: string }>(`/tenants/${tenantId}/roles/${roleId}`, {
            method: 'PUT',
            body: JSON.stringify(body),
        }),

    deleteTenantRole: (tenantId: string, roleId: string) =>
        apiFetch<{ success: true }>(`/tenants/${tenantId}/roles/${roleId}`, { method: 'DELETE' }),

    // ─── Branding del tenant (drill-down) y del PLATFORM ───
    // Endpoint real `GET/PUT /tenants/{tenantId}/branding`. Para el drill-down `tenantId` sale del PATH;
    // para el branding de la PLATAFORMA misma se invoca con `tenantId='platform'` (la ficha platform es un
    // PlatformTenantConfig más → su branding vive en el mismo endpoint). El platform admin (scope PLATFORM
    // '*') alcanza el scope TENANT por rank; el backend re-enforza. Body/response incluyen logoUrl,
    // faviconUrl, colores (HSL) y logoWidth — mismo contrato/normalizer que tenantApi.getBranding/updateBranding.
    getTenantBranding: (tenantId: string): Promise<TenantBrandingLocal> =>
        apiFetch<BrandingConfigWire>(`/tenants/${tenantId}/branding`).then(normalizeBranding),

    updateTenantBranding: (
        tenantId: string,
        body: TenantBrandingLocal,
    ): Promise<TenantBrandingLocal> =>
        apiFetch<BrandingConfigWire>(`/tenants/${tenantId}/branding`, {
            method: 'PUT',
            body: JSON.stringify(body),
        }).then(normalizeBranding),

    // ─── Auditoría PLATFORM ───
    // `GET /platform/audit{querystring}`. NO lleva tenantId en el path (es la bitácora a nivel plataforma).
    // Reusa el shape de params/normalizer del flujo tenant (SharedAuditLogRow). Diferencia de contrato: el
    // cursor de paginación viaja como `nextKey` (no `cursor`) y `from`/`to` son epoch ms. Por eso mapeamos
    // `cursor` → `nextKey` acá; el normalizer ya devuelve `lastEvaluatedKey` = `nextKey` para el getNextPageParam.
    queryAudit: (params: AuditQueryParamsLocal = {}) => {
        const { cursor, ...rest } = params;
        const query: Record<string, string | number | undefined> = {
            ...rest,
            nextKey: cursor,
        };
        return apiFetch<AuditQueryWire>(`/platform/audit${toSearch(query)}`).then(
            normalizeAuditQuery,
        );
    },
};
